import { useState } from "react";
import { createPortal } from "react-dom";
import {
  useOrganizationMemberships,
  useAddMembership,
  useRemoveMembership,
  useUsersList,
  useCreateUser,
  isApiError,
} from "../api";
import type { OrganizationMembership } from "../types";
import { UserPermissionsModal } from "./UserPermissionsModal";
import { ResetPasswordButton } from "./ResetPasswordButton";

interface MembershipManagerProps {
  organizationId: string;
  organizationArchived?: boolean;
}

export function MembershipManager({ organizationId, organizationArchived }: MembershipManagerProps) {
  const { data: memberships, isLoading } = useOrganizationMemberships(organizationId);
  const { data: users } = useUsersList();
  const createUser = useCreateUser();
  const addMembership = useAddMembership(organizationId);
  const removeMembership = useRemoveMembership(organizationId);
  const [userId, setUserId] = useState("");
  const [membershipType, setMembershipType] = useState("member");
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [permissionsModalUser, setPermissionsModalUser] = useState<{ id: string; name: string } | null>(null);

  // New user form state
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [createUserError, setCreateUserError] = useState<string | null>(null);
  const usersList = Array.isArray(users) ? users : [];

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    addMembership.mutate(
      { user_id: userId, membership_type: membershipType },
      { onSuccess: () => setUserId("") },
    );
  };

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault();
    setCreateUserError(null);
    createUser.mutate(
      {
        first_name: newFirstName,
        last_name: newLastName,
        username: newUsername,
        email: newEmail,
        password: newPassword || undefined,
      },
      {
        onSuccess: (newUser: { id: string }) => {
          setUserId(newUser.id);
          setUserModalOpen(false);
          setNewFirstName("");
          setNewLastName("");
          setNewUsername("");
          setNewEmail("");
          setNewPassword("");
        },
        onError: (err: unknown) => {
          if (isApiError(err)) {
            const data = err.response.data;
            setCreateUserError(
              typeof data?.detail === "string"
                ? data.detail
                : typeof data === "object"
                ? JSON.stringify(data)
                : (err as Error).message || "خطا در ایجاد کاربر."
            );
          } else {
            setCreateUserError((err as Error).message || "خطا در ایجاد کاربر.");
          }
        },
      }
    );
  };

  const getRoleLabel = (type: string) => {
    switch (type) {
      case "administrator": return { label: "مدیر ارشد", color: "rgba(124, 58, 237, 0.12)", textColor: "#7c3aed" };
      case "manager": return { label: "مدیر", color: "rgba(37, 99, 235, 0.12)", textColor: "#2563eb" };
      default: return { label: "عضو", color: "rgba(107, 114, 128, 0.12)", textColor: "#4b5563" };
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
        <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span>👥</span> اعضای فعال سازمان
        </h3>
        {!organizationArchived && (
          <button
            type="button"
            onClick={() => setUserModalOpen(true)}
            style={{
              background: "rgba(37, 99, 235, 0.1)",
              border: "1px solid rgba(37, 99, 235, 0.3)",
              color: "#2563eb",
              borderRadius: "6px",
              padding: "0.3rem 0.75rem",
              fontSize: "0.8rem",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.3rem"
            }}
          >
            ➕ کاربر جدید
          </button>
        )}
      </div>

      {!organizationArchived && (
        <form onSubmit={handleAdd} style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          style={{
            flex: 1,
            background: "var(--color-surface, #fff)",
            border: "1px solid var(--color-border, #cbd5e1)",
            borderRadius: "8px",
            padding: "0.45rem 0.75rem",
            fontSize: "0.85rem",
            color: "var(--color-text-main)",
            outline: "none"
          }}
          aria-label="انتخاب کاربر برای افزودن به سازمان"
        >
          <option value="">-- انتخاب کاربر برای افزودن به سازمان --</option>
          {(usersList || []).map((u: { id: string; first_name?: string; last_name?: string; username: string; email: string }) => (
            <option key={u.id} value={u.id}>
              {u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : u.username} ({u.email})
            </option>
          ))}
        </select>
        <select
          value={membershipType}
          onChange={(e) => setMembershipType(e.target.value)}
          style={{
            background: "var(--color-surface, #fff)",
            border: "1px solid var(--color-border, #cbd5e1)",
            borderRadius: "8px",
            padding: "0.45rem 0.75rem",
            fontSize: "0.85rem",
            color: "var(--color-text-main)",
            outline: "none"
          }}
        >
          <option value="member">عضو</option>
          <option value="manager">مدیر</option>
          <option value="administrator">مدیر ارشد</option>
        </select>
        <button
          type="submit"
          disabled={addMembership.isPending || !userId}
          style={{
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            padding: "0.45rem 1rem",
            fontSize: "0.85rem",
            fontWeight: "600",
            cursor: addMembership.isPending || !userId ? "not-allowed" : "pointer",
            opacity: addMembership.isPending || !userId ? 0.6 : 1,
            whiteSpace: "nowrap"
          }}
        >
          افزودن
        </button>
      </form>
      )}

      {organizationArchived && (
        <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "1rem" }}>
          افزودن عضو جدید ممکن نیست (سازمان آرشیو شده است).
        </p>
      )}

      {/* Modal for Creating New User */}
      {userModalOpen && createPortal(
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.6)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1100,
          padding: "1rem"
        }}>
          <form
            onSubmit={handleCreateUser}
            style={{
              background: "var(--color-surface, #ffffff)",
              border: "1px solid var(--color-border, #e2e8f0)",
              borderRadius: "14px",
              padding: "1.5rem",
              width: "100%",
              maxWidth: "400px",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.15)",
              color: "var(--color-text-main)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--color-border, #f1f5f9)", paddingBottom: "0.5rem" }}>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: "700" }}>👤 ایجاد کاربر جدید در مستأجر</h3>
              <button type="button" onClick={() => setUserModalOpen(false)} style={{ background: "none", border: "none", fontSize: "1.1rem", cursor: "pointer" }}>✕</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", width: "100%", boxSizing: "border-box" }}>
              <label style={{ fontSize: "0.8rem", fontWeight: "600", display: "flex", flexDirection: "column", gap: "0.25rem", width: "100%", boxSizing: "border-box" }}>
                نام
                <input
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  placeholder="مثال: علی"
                  style={{ width: "100%", boxSizing: "border-box", padding: "0.45rem", borderRadius: "6px", border: "1px solid var(--color-border, #cbd5e1)", fontSize: "0.85rem", outline: "none" }}
                />
              </label>
              <label style={{ fontSize: "0.8rem", fontWeight: "600", display: "flex", flexDirection: "column", gap: "0.25rem", width: "100%", boxSizing: "border-box" }}>
                نام خانوادگی
                <input
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                  placeholder="مثال: رضایی"
                  style={{ width: "100%", boxSizing: "border-box", padding: "0.45rem", borderRadius: "6px", border: "1px solid var(--color-border, #cbd5e1)", fontSize: "0.85rem", outline: "none" }}
                />
              </label>
            </div>

            <label style={{ fontSize: "0.8rem", fontWeight: "600", display: "flex", flexDirection: "column", gap: "0.25rem", width: "100%", boxSizing: "border-box" }}>
              نام کاربری (یکتا) *
              <input
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                required
                placeholder="مثال: ali_rezaei"
                dir="ltr"
                style={{ width: "100%", boxSizing: "border-box", padding: "0.45rem", borderRadius: "6px", border: "1px solid var(--color-border, #cbd5e1)", fontSize: "0.85rem", outline: "none" }}
              />
            </label>

            <label style={{ fontSize: "0.8rem", fontWeight: "600", display: "flex", flexDirection: "column", gap: "0.25rem", width: "100%", boxSizing: "border-box" }}>
              آدرس ایمیل *
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
                placeholder="مثال: ali.rezaei@faraz.com"
                dir="ltr"
                style={{ width: "100%", boxSizing: "border-box", padding: "0.45rem", borderRadius: "6px", border: "1px solid var(--color-border, #cbd5e1)", fontSize: "0.85rem", outline: "none" }}
              />
            </label>

            <label style={{ fontSize: "0.8rem", fontWeight: "600", display: "flex", flexDirection: "column", gap: "0.25rem", width: "100%", boxSizing: "border-box" }}>
              رمز عبور اولیه (اختیاری)
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="پیش‌فرض: Demo@123456"
                dir="ltr"
                style={{ width: "100%", boxSizing: "border-box", padding: "0.45rem", borderRadius: "6px", border: "1px solid var(--color-border, #cbd5e1)", fontSize: "0.85rem", outline: "none" }}
              />
            </label>

            {createUserError && (
              <p style={{ fontSize: "0.75rem", color: "#dc2626", margin: 0, padding: "0.4rem 0.6rem", background: "rgba(239, 68, 68, 0.1)", borderRadius: "6px" }}>
                {createUserError}
              </p>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
              <button
                type="button"
                onClick={() => setUserModalOpen(false)}
                style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "0.4rem 1rem", fontSize: "0.85rem", cursor: "pointer" }}
              >
                انصراف
              </button>
              <button
                type="submit"
                disabled={createUser.isPending}
                style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: "6px", padding: "0.4rem 1rem", fontSize: "0.85rem", fontWeight: "600", cursor: "pointer" }}
              >
                {createUser.isPending ? "در حال ثبت..." : "ثبت کاربر"}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {addMembership.isError && (
        <p style={{ fontSize: "0.75rem", color: "#dc2626", marginBottom: "0.75rem", padding: "0.4rem 0.6rem", borderRadius: "6px", background: "rgba(239, 68, 68, 0.1)" }}>
          افزودن عضو ناموفق بود — دسترسی «tenant.manage» لازم است یا کاربر عضو فعال دیگری دارد.
        </p>
      )}

      {isLoading && <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>در حال بارگذاری اعضا...</p>}

      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
        {memberships
          ?.filter((m: OrganizationMembership) => m.is_active)
          .map((m: OrganizationMembership) => (
            <li
              key={m.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0.5rem 0.75rem",
                borderRadius: "8px",
                background: "rgba(0,0,0,0.02)",
                border: "1px solid var(--color-border, #f1f5f9)",
                fontSize: "0.85rem"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <span style={{ width: "28px", height: "28px", borderRadius: "50%", background: "rgba(37, 99, 235, 0.1)", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "700", fontSize: "0.75rem" }}>
                  👤
                </span>
                <span style={{ fontWeight: "600", fontSize: "0.85rem" }}>
                  {m.user_details ? `${m.user_details.display_name} (${m.user_details.username} — ${m.user_details.email})` : m.user_id}
                </span>
                <span style={{
                  fontSize: "0.725rem",
                  fontWeight: "600",
                  padding: "0.1rem 0.5rem",
                  borderRadius: "9999px",
                  background: getRoleLabel(m.membership_type).color,
                  color: getRoleLabel(m.membership_type).textColor
                }}>
                  {getRoleLabel(m.membership_type).label}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <button
                  type="button"
                  onClick={() =>
                    setPermissionsModalUser({
                      id: m.user_id,
                      name: m.user_details ? m.user_details.display_name : m.user_id,
                    })
                  }
                  style={{
                    background: "rgba(37, 99, 235, 0.08)",
                    border: "1px solid rgba(37, 99, 235, 0.25)",
                    color: "#2563eb",
                    fontSize: "0.775rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    padding: "0.2rem 0.6rem",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.2rem",
                  }}
                >
                  ⚙️ مجوزها
                </button>

                <ResetPasswordButton
                  userId={m.user_id}
                  userDisplayName={m.user_details ? m.user_details.display_name : m.user_id}
                />

                <button
                  type="button"
                  onClick={() => removeMembership.mutate(m.user_id)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#dc2626",
                    fontSize: "0.775rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    padding: "0.2rem 0.5rem",
                    borderRadius: "4px"
                  }}
                >
                  حذف
                </button>
              </div>
            </li>
          ))}
      </ul>

      {permissionsModalUser && (
        <UserPermissionsModal
          open={!!permissionsModalUser}
          onClose={() => setPermissionsModalUser(null)}
          userId={permissionsModalUser.id}
          userDisplayName={permissionsModalUser.name}
        />
      )}
    </div>
  );
}
